/**
 * API key CRUD hooks. The created secret is returned exactly once by the
 * server — the page must surface it immediately and never re-display it.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

export interface APIKeyItem {
  id: number;
  name: string;
  key_prefix: string;
  last_used_at?: string;
  expires_at?: string;
  revoked_at?: string;
  created_at: string;
}

export interface APIKeyListResponse {
  items: APIKeyItem[];
}

export interface CreateAPIKeyResponse extends APIKeyItem {
  /** Plaintext secret. Visible only on this response — store immediately. */
  secret: string;
}

const KEYS_KEY = ['api-keys'] as const;

export function useAPIKeys() {
  return useQuery({
    queryKey: KEYS_KEY,
    queryFn: () => api.get<APIKeyListResponse>('/v1/api-keys'),
    staleTime: 60_000,
  });
}

export function useCreateAPIKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => api.post<CreateAPIKeyResponse>('/v1/api-keys', { name }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS_KEY });
    },
  });
}

export function useRevokeAPIKey() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/v1/api-keys/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEYS_KEY });
    },
  });
}
